import * as React from "react";
import { MoreHorizontal } from "lucide-react";
import { Slot } from "radix-ui";
import { cn } from "@/shared/utils/utils";
import { Icons } from "@/assets/images";

const Breadcrumb = ({
  ...props
}: React.ComponentProps<"nav">): React.ReactElement => {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />;
};

const BreadcrumbList = ({
  className,
  ...props
}: React.ComponentProps<"ol">): React.ReactElement => {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        "text-muted-foreground flex flex-wrap items-center gap-x-1.5 text-xs xs:text-sm break-words",
        className,
      )}
      {...props}
    />
  );
};

const BreadcrumbItem = ({
  className,
  ...props
}: React.ComponentProps<"li">): React.ReactElement => {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn("inline-flex items-center gap-1.5", className)}
      {...props}
    />
  );
};

const BreadcrumbLink = ({
  asChild,
  className,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean;
}): React.ReactElement => {
  const Comp = asChild ? Slot.Root : "a";

  return (
    <Comp
      data-slot="breadcrumb-link"
      className={cn(
        "hover:text-primary transition-colors text-breadcrumb",
        className,
      )}
      {...props}
    />
  );
};

const BreadcrumbPage = ({
  className,
  ...props
}: React.ComponentProps<"span">): React.ReactElement => {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn("text-breadcrumb font-medium tracking-wide", className)}
      {...props}
    />
  );
};

const BreadcrumbSeparator = ({
  className,
  ...props
}: React.ComponentProps<"li">): React.ReactElement => {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn("[&>svg]:size-3.5", className)}
      {...props}
    >
      <div className="w-3 h-3">
        <img
          className="object-cover"
          src={Icons.chevronRightIcon}
          alt="Right Arrow"
        />
      </div>
    </li>
  );
};

const BreadcrumbEllipsis = ({
  className,
  ...props
}: React.ComponentProps<"span">): React.ReactElement => {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn("flex size-9 items-center justify-center", className)}
      {...props}
    >
      <MoreHorizontal className="size-4" />
      <span className="sr-only">More</span>
    </span>
  );
};

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
};
